// Audit · Compliance & Audit home. HomeHero + 6 KPIs + security/critical events
// table + license-lifecycle feed. Data: /users/audit-logs, /analytics/license-events,
// /analytics/validations-log. Severity/actor are derived classifications of the
// real action string; the compliance score is a transparent computed metric
// (100 − 2·critical7d − failedValidations24h), never a hardcoded figure.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { HomeHero } from '@/components/shared/HomeHero';
import { Kpi } from '@/components/shared/Kpi';
import { Card, CardHead, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { SevPill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { PERSONA } from '@/lib/persona';
import {
  getAuditLogs, getLicenseEvents, getValidationsLog,
  severityOf, severityRank, actorLabelOf, isAccessChange,
  type AuditLogRow, type LicenseEventRow, type ValidationLogRow,
} from '@/lib/api/audit';

const ACCENT = PERSONA.audit.accent;
const DAY_MS = 86_400_000;

const LIFECYCLE: Record<string, { icon: string; color: string; bg: string }> = {
  issued: { icon: 'plus-circle', color: 'var(--ps-pill-green-tx)', bg: 'var(--ps-pill-green-bg)' },
  activated: { icon: 'power', color: 'var(--ps-pill-green-tx)', bg: 'var(--ps-pill-green-bg)' },
  renewed: { icon: 'refresh-cw', color: 'var(--ps-pill-blue-tx)', bg: 'var(--ps-pill-blue-bg)' },
  reinstated: { icon: 'play-circle', color: 'var(--ps-pill-blue-tx)', bg: 'var(--ps-pill-blue-bg)' },
  escalated: { icon: 'arrow-up-circle', color: 'var(--ps-pill-blue-tx)', bg: 'var(--ps-pill-blue-bg)' },
  revoked: { icon: 'ban', color: 'var(--ps-pill-red-tx)', bg: 'var(--ps-pill-red-bg)' },
  suspended: { icon: 'pause-circle', color: 'var(--ps-pill-amber-tx)', bg: 'var(--ps-pill-amber-bg)' },
  expired: { icon: 'clock', color: 'var(--ps-pill-amber-tx)', bg: 'var(--ps-pill-amber-bg)' },
  validated: { icon: 'badge-check', color: 'var(--ps-pill-gray-tx)', bg: 'var(--ps-pill-gray-bg)' },
};
function lifecycleSkin(type: string) {
  return LIFECYCLE[type?.toLowerCase()] || { icon: 'circle', color: 'var(--text-muted)', bg: 'var(--bg-muted)' };
}

function timeOnly(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}
function dateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export function AuditHome() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [events, setEvents] = useState<LicenseEventRow[]>([]);
  const [denied, setDenied] = useState<ValidationLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [l, e, v] = await Promise.all([
        getAuditLogs(200),
        getLicenseEvents(100).catch(() => []),
        getValidationsLog({ denied: true, limit: 200 }).catch(() => []),
      ]);
      setLogs(l);
      setEvents(e);
      setDenied(v);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit data');
      setLogs([]); setEvents([]); setDenied([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const now = Date.now();
  const within = (iso: string, ms: number) => now - new Date(iso).getTime() <= ms;
  const startOfToday = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); }, []);

  const eventsToday = logs.filter((l) => new Date(l.created_at).getTime() >= startOfToday).length;
  const critical7d = logs.filter((l) => within(l.created_at, 7 * DAY_MS) && severityOf(l.action) === 'critical').length;
  const adminActions7d = logs.filter((l) => within(l.created_at, 7 * DAY_MS) && !l.api_key_id && l.user_id).length;
  const accessChanges7d = logs.filter((l) => within(l.created_at, 7 * DAY_MS) && isAccessChange(l.action)).length;
  const failedVal24h = denied.filter((v) => within(v.timestamp, DAY_MS)).length;
  const complianceScore = Math.max(0, Math.min(100, 100 - 2 * critical7d - failedVal24h));

  const securityEvents = useMemo(
    () => [...logs]
      .filter((l) => { const s = severityOf(l.action); return s === 'critical' || s === 'high'; })
      .sort((a, b) => {
        const r = severityRank(severityOf(b.action)) - severityRank(severityOf(a.action));
        return r !== 0 ? r : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      })
      .slice(0, 5),
    [logs],
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <div className="animate-spin" style={{ width: 32, height: 32, border: '2px solid var(--persona-audit)', borderTopColor: 'transparent', borderRadius: '50%' }} />
      </div>
    );
  }

  return (
    <div>
      <HomeHero
        persona="audit"
        title="Compliance & Audit"
        subtitle="A complete, tamper-evident trail of everything that happened"
        actions={
          <>
            <Btn icon="download" disabled title="Evidence export is not yet supported.">Export evidence</Btn>
            <Btn kind="primary" accent={ACCENT} icon="file-text" disabled title="Report generation is not yet supported.">Generate report</Btn>
          </>
        }
      />

      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">Unable to load audit data: {error}</AlertBanner></div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 14 }}>
        <Kpi label="Compliance Score" value={complianceScore} unit="/100" accent={ACCENT} icon="shield-check" />
        <Kpi label="Events Today" value={eventsToday} hint="across all actors" accent={ACCENT} icon="activity" onClick={() => navigate('/audit/logs')} />
        <Kpi label="Critical Events (7d)" value={critical7d} hint={critical7d > 0 ? 'review needed' : 'all clear'} accent={ACCENT} icon="alert-octagon" onClick={() => navigate('/audit/logs')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 }}>
        <Kpi label="Admin Actions (7d)" value={adminActions7d} accent={ACCENT} icon="user-cog" />
        <Kpi label="Access Changes (7d)" value={accessChanges7d} hint="role / key grants" accent={ACCENT} icon="user-check" onClick={() => navigate('/audit/access')} />
        <Kpi label="Failed Validations (24h)" value={failedVal24h} hint={failedVal24h > 0 ? 'anomalies' : 'none'} accent={ACCENT} icon="badge-x" onClick={() => navigate('/audit/validations')} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        {/* Security & critical events */}
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="Security & critical events" sub="Highest severity first" right={<a onClick={() => navigate('/audit/logs')} style={linkA}>Full log <Icon name="arrow-right" size={12} /></a>} />
          {securityEvents.length === 0 ? (
            <div style={empty}>No critical or high-severity events.</div>
          ) : (
            <Table cols={[{ label: 'When' }, { label: 'Severity' }, { label: 'Action' }, { label: 'Actor' }]}>
              {securityEvents.map((e) => (
                <Row key={e.id}>
                  <Td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{timeOnly(e.created_at)}</Td>
                  <Td><SevPill level={severityOf(e.action)} /></Td>
                  <Td>
                    <Mono style={{ fontWeight: 600 }}>{e.action}</Mono>
                    {(e.resource_type || e.resource_id) && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{[e.resource_type, e.resource_id].filter(Boolean).join(' · ')}</div>}
                  </Td>
                  <Td><span style={{ fontSize: 12.5 }}>{actorLabelOf(e)}</span></Td>
                </Row>
              ))}
            </Table>
          )}
        </Card>

        {/* License lifecycle */}
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="License lifecycle" sub="Issued · renewed · revoked" right={<a onClick={() => navigate('/audit/lifecycle')} style={linkA}>All <Icon name="arrow-right" size={12} /></a>} />
          <div style={{ padding: '8px 18px 12px' }}>
            {events.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No license events yet.</div>
            ) : (
              events.slice(0, 6).map((l, i) => {
                const lc = lifecycleSkin(l.event_type);
                const org = l.license?.organization?.name || l.license?.license_id || 'Unknown';
                const reason = (l.event_data?.reason as string) || (l.event_data?.note as string) || '';
                return (
                  <div key={l.id} style={{ display: 'flex', gap: 12, padding: '11px 0', borderBottom: i < Math.min(events.length, 6) - 1 ? '1px solid var(--bg-muted)' : 'none' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: lc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={lc.icon} size={14} color={lc.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}><span style={{ fontWeight: 600, textTransform: 'capitalize', color: lc.color }}>{l.event_type}</span> · {org}</div>
                      {reason && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>{reason}</div>}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{dateTime(l.created_at)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const linkA: React.CSSProperties = { fontSize: 12.5, color: 'var(--ps-pill-purple-tx)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' };
const empty: React.CSSProperties = { padding: '28px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 };

export default AuditHome;
